/**
 * App-side wiring for the Heidi II surfaces behind `@heidi/agents`.
 *
 * Every member crosses BY REFERENCE and is called where it was called before
 * the move. That matters more here than it usually does: `Heidi2AwarenessBanner`
 * renders on every page and the nudges render conditionally, so resolving a
 * value at a mount would move a PostHog exposure or a query to a render that
 * does not happen today.
 *
 * TWO INPUTS ARE DELIBERATELY ABSENT, and both are weight rather than taste.
 * `useHeidi2AccessControl` is bound inside the lazily-loaded confirm-access
 * mount (`global-modals-cloud-only-heidi-2-confirm-access.tsx`), which is where
 * it already was; and the "Get started" checklist's progress inputs cross as a
 * `ports` prop from `agent-home-panel.tsx`, its only mount. Binding either here
 * would pull its module into `_app`'s eager cone for every page load.
 *
 * It imports `@heidi/agents/data` and never the `.` UI door: this module is
 * evaluated at `_app` module scope, and a static `.` import here would put that
 * door in the eager graph.
 *
 * Imported for effect at module scope in `_app.tsx`.
 */
import { isHomeBootPath } from '@/atoms/bootLandingAtoms';
import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import {
  currentQueuedModalAtom,
  modalQueueAtom,
  seenModalIdsAtom,
} from '@/atoms/modalQueueAtoms';
import { useReportTopStrip } from '@/atoms/top-strip-atoms';
import { useChatSidebarHandlers } from '@/components/layout/Navigation/hooks/useChatSidebarHandlers';
import { useAgentHomePanel } from '@/components/layout/Navigation/nav-v3/agent-home-panel-state';
import {
  useHeidi2WelcomeCtaCopy,
  useIsHeidi2OnboardingEnabled,
  useIsHeidi2OptInEnabled,
} from '@/features/flags';
import { getEhrLogoUrl } from '@/helpers/getEhrLogoUrl';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useQueryCreditAllowance } from '@/hooks/query/useQueryCreditAllowance';
import { useQueryRoles } from '@/hooks/query/useQueryRoles';
import { useQueryTeamAccessEntitlements } from '@/hooks/query/useQueryTeamAccessEntitlements';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { useUserCanonicalSpecialtyName } from '@/hooks/useUserCanonicalSpecialtyName';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackModalViewed } from '@/lib/analytics/scribe/ui';
import { isModalClosed } from '@/lib/modalClosedRules';
import {
  trackHeidi2AwarenessBannerViewed,
  trackHeidi2EvidenceTourStepViewed,
  trackHeidi2ExploreLaterBannerViewed,
  trackHeidi2GetStartedChecklistViewed,
  trackHeidi2NewUserHomeOnboardingBannerViewed,
  trackHeidi2RequestAccessBannerViewed,
} from '@/models/analytics/features/heidi-2-explore-later-analytics';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import {
  configureHeidi2Ports,
  pendingWorkHomeViewAtom,
  useBaseChatHref,
  type Heidi2Ports,
} from '@heidi/agents/data';
import {
  isMaxBillingSeat,
  useWorkspaceIdSettledForCreditUsagePopoverLaunch,
} from '@heidi/commerce';
import {
  isComplimentaryCreditAllowance,
  useMutateActivateHeidi2,
  useMutateRequestHeidi2Access,
  useUserBillingInfo,
} from '@heidi/commerce/data';
import { NANGO_SETTINGS_PATH } from '@heidi/integrations/connected-apps/contracts';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

/** The queue head's id, as a named hook so the rules of hooks apply through it. */
const useCurrentQueuedModalId = () => useAtomValue(currentQueuedModalAtom)?.id;

/** The UNFILTERED queue head — the modal that would show next, not the one showing. */
const useQueueHeadModalId = () => useAtomValue(modalQueueAtom).at(0)?.id;

/** The three launch-state fields the Heidi II welcome cohort reads. */
const useModalLaunchState = () => {
  const { isUserDataLoaded, isUserPending, isKindePropertiesPending } =
    useAtomValue(appStateForModalsAtom);
  return { isUserDataLoaded, isUserPending, isKindePropertiesPending };
};

/** Stages the Work-home view so the tour's finish lands on Home. */
const useSetPendingWorkHomeView = () => useSetAtom(pendingWorkHomeViewAtom);

/** The connected EHR's logo, or `undefined` for the generic tile. */
const useEhrLogoSrc = () => getEhrLogoUrl(useIntegrationEhrInfo().integrationEhrProvider);

/** The backend-synced seen set, reactively — the Home dot re-renders on hydration. */
const useSeenModalIds = () => useAtomValue(seenModalIdsAtom);

/** `router.pathname`. The route itself stays app-owned; only the string crosses. */
const usePathname = () => useRouter().pathname;

/** `router.asPath` — where the tour puts someone back when they decline it. */
const useRouterAsPath = () => useRouter().asPath;

/** The Home panel opener, the same one the Home nav item uses. */
const useOpenHomePanel = () => useAgentHomePanel().open;

/** `router.push`, so the checklist's skills item keeps its shallow navigation. */
const useNavigate = () => {
  const router = useRouter();
  return (href: string, options?: { shallow?: boolean }) =>
    void router.push(href, undefined, options);
};

/** The mark-as-viewed write, unwrapped from the mutation object. */
const useMarkModalAsViewed = () => useMutateMarkModalAsViewed().mutate;

export const heidi2Ports: Heidi2Ports = {
  useIsHeidi2OnboardingEnabled,
  useUserBillingInfo,
  useQueryUser,
  useSeenModalIds,
  isModalClosed,
  useQueuedModal,
  useCurrentQueuedModalId,
  useQueueHeadModalId,
  useModalLaunchState,
  useMarkModalAsViewed,
  useMutateRequestHeidi2Access,
  usePathname,
  useOpenHomePanel,
  useNavigate,
  useChatSidebarHandlers,
  connectAppsHref: NANGO_SETTINGS_PATH,
  useRouterAsPath,
  useBaseChatHref,
  isHomeBootPath,
  useSetPendingWorkHomeView,
  useReportTopStrip,
  useIsHeidi2OptInEnabled,
  useHeidi2WelcomeCtaCopy,
  useWorkspaceIdSettledForCreditUsagePopoverLaunch,
  useQueryRoles,
  useQueryTeamAccessEntitlements,
  isMaxBillingSeat,
  useUserCanonicalSpecialtyName,
  useEhrLogoSrc,
  useMutateActivateHeidi2,
  useIsClinicalRecordingActive,
  useCreditAllowance: useQueryCreditAllowance,
  isComplimentaryCreditAllowance,
  isAppleCompliantBuild,
  isOnPrem,
  trackButtonClicked,
  trackModalViewed,
  trackHeidi2AwarenessBannerViewed,
  trackHeidi2RequestAccessBannerViewed,
  trackHeidi2EvidenceTourStepViewed,
  trackHeidi2ExploreLaterBannerViewed,
  trackHeidi2GetStartedChecklistViewed,
  trackHeidi2NewUserHomeOnboardingBannerViewed,
};

configureHeidi2Ports(heidi2Ports);
